import * as React from "react";
import type { AddInHistorySuggestion, SaveAndInsertSuggestionArgs } from "../../types/chat";
import { showPluginToast } from "../shell/PluginToast";
import { ChatMicroBtn } from "./ui/ChatMicroBtn";
import { ChatOutlineBtn } from "./ui/ChatOutlineBtn";
import { IconCopy, IconEdit } from "./icons/ChatIcons";

function suggestionCardKey(messageId: string, index: number): string {
  return `${messageId}-s-${index}`;
}

function estimateTextAreaRows(text: string): number {
  const lineCount = text.split("\n").length;
  const wrapped = Math.ceil(text.length / 52);
  return Math.min(8, Math.max(2, Math.max(lineCount, wrapped)));
}

export type ChatSuggestionCardProps = {
  messageId: string;
  messageIndex: number;
  suggestionIndex: number;
  suggestion: AddInHistorySuggestion;
  showActionBar: boolean;
  editingKey: string | null;
  editedTextByKey: Record<string, string>;
  busyKey: string | null;
  onEnterEdit: (key: string, plain: string) => void;
  onExitEdit: (key: string) => void;
  onEditedTextChange: (key: string, value: string) => void;
  onBusy: (key: string | null) => void;
  onInsertSuggestion: (suggestion: AddInHistorySuggestion, suggestionIndex: number) => void | Promise<void>;
  onSaveAndInsertSuggestion: (args: SaveAndInsertSuggestionArgs) => Promise<void>;
};

export const ChatSuggestionCard: React.FC<ChatSuggestionCardProps> = ({
  messageId,
  messageIndex,
  suggestionIndex: index,
  suggestion: s,
  showActionBar,
  editingKey,
  editedTextByKey,
  busyKey,
  onEnterEdit,
  onExitEdit,
  onEditedTextChange,
  onBusy,
  onInsertSuggestion,
  onSaveAndInsertSuggestion,
}) => {
  const key = suggestionCardKey(messageId, index);
  const isEditing = editingKey === key;
  const busy = busyKey === key;
  const plainSuggestion = s.text;
  const showEditingUi = isEditing && showActionBar;
  const copyText = (editedTextByKey[key] ?? plainSuggestion).trim() || plainSuggestion;

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(copyText);
      showPluginToast("Copied to clipboard");
    } catch {
      showPluginToast("Could not copy to clipboard");
    }
  };

  const handleSaveAndInsert = () => {
    const editedText = (editedTextByKey[key] ?? plainSuggestion).trim();
    if (!editedText || busy) return;
    onBusy(key);
    void (async () => {
      try {
        await onSaveAndInsertSuggestion({
          messageId,
          messageIndex,
          suggestionIndex: index,
          editedText,
        });
        onExitEdit(key);
      } finally {
        onBusy(null);
      }
    })();
  };

  return (
    <div className="chat-suggestion-card">
      <span className="chat-suggestion-card__label">Suggestion {index + 1}</span>
      <span className="chat-suggestion-card__copy">
        <ChatMicroBtn title="Copy suggestion" aria-label="Copy suggestion" onClick={() => void handleCopy()}>
          <IconCopy size={12} />
        </ChatMicroBtn>
      </span>

      <div style={{ width: "100%", minWidth: 0 }}>
        {showEditingUi ? (
          <textarea
            className="chat-suggestion-card__editor"
            value={editedTextByKey[key] ?? plainSuggestion}
            disabled={busy}
            onChange={(e) => onEditedTextChange(key, e.target.value)}
            rows={estimateTextAreaRows(editedTextByKey[key] ?? plainSuggestion)}
          />
        ) : (
          <span className="chat-suggestion-card__text">{plainSuggestion}</span>
        )}
      </div>

      {showActionBar ? (
        <div className="chat-suggestion-card__actions">
          {showEditingUi ? (
            <>
              <ChatOutlineBtn primary disabled={busy} onClick={handleSaveAndInsert}>
                {busy ? "Saving…" : "Save & Insert"}
              </ChatOutlineBtn>
              <ChatOutlineBtn disabled={busy} onClick={() => onExitEdit(key)}>
                Cancel
              </ChatOutlineBtn>
            </>
          ) : (
            <>
              <ChatOutlineBtn
                disabled={busy}
                onClick={() => onEnterEdit(key, plainSuggestion)}
              >
                <IconEdit size={12} />
                Edit
              </ChatOutlineBtn>
              <ChatOutlineBtn
                primary
                disabled={busy}
                onClick={() => void onInsertSuggestion(s, index)}
              >
                Insert
              </ChatOutlineBtn>
            </>
          )}
        </div>
      ) : null}
    </div>
  );
};

export { suggestionCardKey };
